import React, { createContext, useContext, useState, useEffect } from 'react'

type Language = 'de' | 'en'

interface LanguageContextType {
  language: Language
  setLanguage: (lang: Language) => void
  t: (key: string) => string
}

const translations = {
  de: {
    // Header
    'nav.login': 'Anmelden',
    'nav.beta': 'Beta beitreten',
    'nav.home': 'Start',

    // Groups (coach view)
    'groups.title': 'Gruppen-Training',
    'groups.subtitle': 'Erstelle Gruppen, verwalte Mitglieder und generiere optimierte Gruppen-Sessions.',
    'groups.create.title': 'Neue Gruppe',
    'groups.create.namePlaceholder': 'Gruppenname',
    'groups.create.submit': 'Gruppe erstellen',
    'groups.empty': 'Noch keine Gruppen. Erstelle deine erste Gruppe.',
    'groups.members.title': 'Mitglieder',
    'groups.members.addPlaceholder': 'Mitglieds-Benutzer-ID',
    'groups.members.add': 'Hinzufügen',
    'groups.members.remove': 'Entfernen',
    'groups.members.empty': 'Noch keine Mitglieder.',
    'groups.generate': 'Gruppen-Session generieren',
    'groups.generating': 'Generiere…',
    'groups.session.title': 'Letzte generierte Session',
    'groups.error.generic': 'Etwas ist schiefgelaufen. Bitte versuche es erneut.',
    'groups.error.needTwo': 'Eine Gruppe benötigt mindestens zwei Mitglieder.',

    // Homepage
    'home.hero.title1': 'Die Zukunft des',
    'home.hero.title2': 'Muay Thai Trainings',
    'home.hero.subtitle': 'Hilf uns dabei, die erste KI für Muay Thai Training zu entwickeln. Deine Trainingsdaten werden zur Entwicklung intelligenter Trainingspläne verwendet.',
    'home.hero.cta.beta': 'Beta beitreten',
    'home.hero.cta.login': 'Anmelden',

    'home.features.title': 'Intelligentes Training',
    'home.features.subtitle': 'TrainIQ analysiert deine Trainingsdaten und erstellt personalisierte Pläne, die sich an deinen Fortschritt anpassen.',

    'home.feature1.title': 'KI-gestützte Analyse',
    'home.feature1.desc': 'Unsere KI analysiert deine Trainingsgewohnheiten, Fortschritte und Schwachstellen, um optimale Trainingspläne zu erstellen.',

    'home.feature2.title': 'Personalisierte Pläne',
    'home.feature2.desc': 'Jeder Trainingsplan wird individuell auf dein Erfahrungslevel, deine Ziele und verfügbare Zeit abgestimmt.',

    'home.feature3.title': 'Adaptive Entwicklung',
    'home.feature3.desc': 'Die KI lernt kontinuierlich aus deinen Trainingsdaten und passt Empfehlungen an deinen Fortschritt an.',

    'home.beta.badge': 'Exklusiv für ausgewählte TKBC-Mitglieder',
    'home.beta.title': 'Sei Teil der Revolution',
    'home.beta.subtitle': 'Hilf uns dabei, die erste KI für Muay Thai Training zu entwickeln. Erhalte exklusiven Zugang zu neuen Features und gestalte die Zukunft mit.',
    'home.beta.cta': 'Jetzt Beta beitreten',

    'home.status.title': 'Aktueller Entwicklungsstand',
    'home.status.phase': 'Phase 1: Beta Training Tracking & Datensammlung',
    'home.status.desc': 'Wir sammeln derzeit Trainingsdaten von Beta-Nutzern, um unsere KI zu trainieren. Personalisierte Trainingspläne und Analysen folgen in den nächsten Phasen.',

    // Beta Page
    'beta.badge': 'Exklusives Beta-Programm für ausgewählte TKBC-Mitglieder',
    'beta.title': 'Muay-Thai.app Training Intelligence',
    'beta.subtitle': 'Hilf uns dabei, die erste KI für Muay Thai Training zu entwickeln. Deine Trainingsdaten helfen uns, intelligente Trainingspläne und Fortschrittsanalysen zu erstellen.',

    'beta.feature1.title': 'Training Tracking',
    'beta.feature1.desc': 'Erfasse deine Trainings mit Dauer, Inhalten und Notizen. Simple und schnelle Eingabe für regelmäßige Nutzung.',

    'beta.feature2.title': 'KI-Entwicklung',
    'beta.feature2.desc': 'Deine anonymisierten Daten fließen in die Entwicklung von personalisierten Trainingsplänen und Fortschrittsanalysen ein.',

    'beta.feature3.title': 'TKBC Community Beta',
    'beta.feature3.desc': 'Exklusiv für ausgewählte TKBC-Mitglieder: Sei Teil der ersten Gruppe, die die Zukunft des intelligenten Muay Thai Trainings mitgestaltet.',

    'beta.privacy.title': 'Moderation & Datenschutz',
    'beta.privacy.approval': 'Manuelle Freigabe: Jede Registrierung wird manuell geprüft und freigeschaltet. Du erhältst eine Bestätigung, sobald dein Zugang aktiviert wurde.',
    'beta.privacy.data': 'Datenschutz: Alle Trainingsdaten werden verschlüsselt gespeichert und ausschließlich für die KI-Entwicklung verwendet. Keine Weitergabe an Dritte.',
    'beta.privacy.anon': 'Anonymisierung: Persönliche Daten werden von Trainingsdaten getrennt und für die KI-Analyse anonymisiert verarbeitet.',
    'beta.privacy.delete': 'Löschung: Du kannst jederzeit die Löschung deiner Daten beantragen. Kontakt: beta@muay-thai.app',

    'beta.steps.title': 'So funktioniert es',
    'beta.step1.title': 'Registrieren',
    'beta.step1.desc': 'Erstelle dein Beta-Profil mit Erfahrungslevel und Trainingszielen.',
    'beta.step2.title': 'Freischaltung',
    'beta.step2.desc': 'Warte auf die manuelle Freigabe durch unser Team (meist binnen 24h).',
    'beta.step3.title': 'Training tracken',
    'beta.step3.desc': 'Erfasse deine Trainings mit Dauer, Inhalt und persönlichen Notizen.',
    'beta.step4.title': 'KI entwickeln',
    'beta.step4.desc': 'Deine Daten helfen uns, intelligente Trainingspläne zu entwickeln.',

    'beta.tracker.title': 'Beta Training Tracking',
    'beta.tracker.subtitle': 'Tracke deine Muay Thai Trainings für die KI-Entwicklung',

    'beta.info.note': 'Hinweis: Diese Beta-Version dient ausschließlich der Datensammlung für die KI-Entwicklung. Trainingspläne und Analysen sind noch nicht verfügbar.',

    // Training Tracker
    'training.title': 'Training Tracking',
    'training.hello': 'Hallo',
    'training.trackSimple': 'Tracke einfach dein Training.',
    'training.addTraining': 'Training hinzufügen',
    'training.whatTrained': 'Was hast du trainiert?',
    'training.placeholder': 'z.B. Aufwärmen 15min, Jab-Cross-Low-Kick 3x3min, Cool-down...',
    'training.duration': 'Dauer',
    'training.minutes': 'Minuten',
    'training.date': 'Datum',
    'training.today': 'Heute',
    'training.notes': 'Notizen (optional)',
    'training.notesPlaceholder': 'Wie lief es? Besonderheiten?',
    'training.rating': 'Training bewerten',
    'training.goalHelp': 'Hat das Training bei deinen Zielen geholfen?',
    'training.goalHelpful': 'Hat geholfen',
    'training.goalNeutral': 'Neutral',
    'training.goalNotHelpful': 'Hat nicht geholfen',
    'training.saveTraining': 'Training speichern',
    'training.recentTrainings': 'Deine letzten Trainings',
    'training.statistics': 'Statistik',
    'training.totalTrainings': 'Trainings insgesamt',
    'training.minutesTrained': 'Minuten trainiert',
    'training.templates': 'Vorlagen verwenden',
    'training.useTemplate': 'Vorlage nutzen',

    // Trainer Feedback
    'trainer.feedback.title': 'Trainer-Feedback',
    'trainer.feedback.submit': 'Trainer-Feedback absenden',
    'trainer.feedback.success': 'Feedback erfolgreich uebermittelt!',
    'trainer.feedback.error': 'Fehler beim Uebermitteln des Feedbacks',
    'trainer.feedback.client': 'Teilnehmer',
    'trainer.feedback.session': 'Session',
    'trainer.feedback.validationSelection': 'Bitte Session und Teilnehmer auswaehlen.',
    'trainer.feedback.validationNewSession': 'Bitte gib fuer die neue Session einen Titel mit mindestens 3 Zeichen ein.',
    'trainer.feedback.validationGuestAlias': 'Bitte gib fuer den Gast mindestens 2 Zeichen als Namen ein.',
    'trainer.feedback.validationScores': 'Alle Score-Felder muessen zwischen 1 und 10 liegen.',
    'trainer.feedback.validationWeeks': 'Wochen bis Meilenstein muessen zwischen 1 und 52 liegen.',
    'trainer.feedback.validationActionable': 'Bitte mindestens eine konkrete Empfehlung oder Beobachtung erfassen.',

    'trainer.feedback.clientPerformance': 'Teilnehmerleistung',
    'trainer.feedback.techniqueQuality': 'Technik-Qualitaet',
    'trainer.feedback.effortLevel': 'Einsatzniveau',
    'trainer.feedback.improvementVisible': 'Verbesserung sichtbar',
    'trainer.feedback.areasExcelling': 'Staerken',
    'trainer.feedback.areasNeedingWork': 'Verbesserungsbereiche',

    'trainer.feedback.sessionEvaluation': 'Session-Bewertung',
    'trainer.feedback.difficulty': 'Schwierigkeit',
    'trainer.feedback.tooEasy': 'Zu leicht',
    'trainer.feedback.optimal': 'Optimal',
    'trainer.feedback.tooHard': 'Zu schwer',
    'trainer.feedback.duration': 'Dauer',
    'trainer.feedback.tooShort': 'Zu kurz',
    'trainer.feedback.tooLong': 'Zu lang',
    'trainer.feedback.exercisesEffective': 'Uebungen waren effektiv',
    'trainer.feedback.progressionSuitable': 'Progression war angemessen',
    'trainer.feedback.safetyConcerns': 'Sicherheitsbedenken',

    'trainer.feedback.progressAssessment': 'Fortschrittsbewertung',
    'trainer.feedback.skillImprovement': 'Technikfortschritt',
    'trainer.feedback.fitnessImprovement': 'Fitnessfortschritt',
    'trainer.feedback.motivationLevel': 'Motivationsniveau',
    'trainer.feedback.consistencyRating': 'Konstanz',
    'trainer.feedback.goalProgress': 'Zielfortschritt',

    'trainer.feedback.recommendations': 'Empfehlungen',
    'trainer.feedback.nextSessionFocus': 'Fokus naechste Session',
    'trainer.feedback.adjustmentsNeeded': 'Noetige Anpassungen',
    'trainer.feedback.equipmentSuggestions': 'Equipment-Empfehlungen',
    'trainer.feedback.timelineAdjustments': 'Zeitplan-Anpassung',
    'trainer.feedback.accelerate': 'Beschleunigen',
    'trainer.feedback.maintain': 'Beibehalten',
    'trainer.feedback.slowDown': 'Verlangsamen',

    'trainer.feedback.coachingNotes': 'Coaching-Notizen',
    'trainer.feedback.positiveObservations': 'Positive Beobachtungen',
    'trainer.feedback.areasForImprovement': 'Bereiche zur Verbesserung',
    'trainer.feedback.motivationalInsights': 'Motivationale Erkenntnisse',
    'trainer.feedback.technicalCorrections': 'Technische Korrekturen',
    'trainer.feedback.overallAssessment': 'Gesamteinschaetzung',

    'trainer.feedback.futurePlanning': 'Zukunftsplanung',
    'trainer.feedback.periodizationPhase': 'Periodisierungsphase',
    'trainer.feedback.build': 'Aufbau',
    'trainer.feedback.peak': 'Peak',
    'trainer.feedback.deload': 'Deload',
    'trainer.feedback.weeksUntilMilestone': 'Wochen bis zum naechsten Meilenstein',
    'trainer.feedback.priorityFocus': 'Prioritaetsfokus',
    'trainer.feedback.priorityFocusPlaceholder': 'z.B. Power-Entwicklung, Technikverfeinerung',
    'trainer.feedback.clientModeKnown': 'Bestehender Client',
    'trainer.feedback.clientModeManual': 'Manuell',
    'trainer.feedback.clientModeGuest': 'Gast-Modus',
    'trainer.feedback.sessionModeKnown': 'Bestehende Session',
    'trainer.feedback.sessionModeNew': 'Neue Session',
    'trainer.feedback.newSessionTitle': 'Session Titel',
    'trainer.feedback.newSessionTitlePlaceholder': 'z.B. Technikblock Dienstag Abend',
    'trainer.feedback.newSessionDate': 'Session Datum',
    'trainer.feedback.newSessionGeneratedId': 'Generierte Session-ID',
    'trainer.feedback.clientNameOptional': 'Client Name (optional)',
    'trainer.feedback.clientNamePlaceholder': 'Client Name',
    'trainer.feedback.guestAliasPlaceholder': 'Gastname eingeben (z.B. Probetraining Max)',
    'trainer.feedback.guestGeneratedId': 'Generierte Gast-ID',

    // Schedule / Live Session
    'schedule.liveSession': 'Jetzt-Session anlegen',
    'schedule.scheduleMatch': 'Stundenplan-Match',
    'schedule.noSchedule': 'Kein Stundenplan fuer diesen Tag hinterlegt.',
    'schedule.freeSession': 'Freie Session',
    'schedule.startNow': 'Jetzt-Session starten',
    'schedule.createAndFeedback': 'Session anlegen & Feedback starten',
    'schedule.cancel': 'Abbrechen',
    'schedule.title': 'Session-Titel',
    'schedule.date': 'Datum',
    'schedule.duration': 'Dauer (Min.)',
    'schedule.noMatch': 'Kein passender Stundenplan-Eintrag gefunden. Session wird als freie Session erstellt.',
    'schedule.scheduleFor': 'Stundenplan',

    // Login Page
    'login.title': 'Anmelden',
    'login.subtitle': 'Zugang zur Beta-Anwendung',
    'login.email': 'E-Mail-Adresse',
    'login.password': 'Passwort',
    'login.submit': 'Anmelden',
    'login.loading': 'Anmelden...',
    'login.noAccount': 'Noch kein Zugang?',
    'login.toBeta': 'Zur Beta-Registrierung',
    'login.error.awaitApproval': 'Dein Zugang ist noch nicht freigeschaltet. Wir aktivieren ihn innerhalb von 24 Stunden und informieren dich per E-Mail.',
    'login.error.emailDisabled': 'Die E-Mail-Anmeldung ist vorübergehend deaktiviert. Bitte kontaktiere beta@trainiq.com.',
    'login.error.generic': 'Anmeldung fehlgeschlagen:',

    'login.success.title': 'Angemeldet!',
    'login.success.welcome': 'Willkommen zurück,',
    'login.success.toBeta': 'Zur Beta-Anwendung',
    'login.success.logout': 'Abmelden',

    // Register Page
    'register.title': 'Beta-Registrierung',
    'register.subtitle': 'Exklusiver Zugang zur TrainIQ Beta',
    'register.email': 'E-Mail-Adresse',
    'register.emailPlaceholder': 'deine@email.de',
    'register.password': 'Passwort',
    'register.passwordPlaceholder': 'Mindestens 8 Zeichen',
    'register.confirmPassword': 'Passwort bestätigen',
    'register.confirmPasswordPlaceholder': 'Passwort wiederholen',
    'register.submit': 'Registrieren',
    'register.registering': 'Registrierung...',
    'register.hasAccount': 'Bereits registriert?',
    'register.loginLink': 'Hier anmelden',

    'register.error.fillFields': 'Bitte alle Felder ausfüllen',
    'register.error.passwordMismatch': 'Passwörter stimmen nicht überein',
    'register.error.passwordLength': 'Passwort muss mindestens 8 Zeichen haben',
    'register.error.tooManyAttempts': 'Zu viele Versuche. Bitte warten Sie 15 Minuten.',
    'register.error.emailExists': 'E-Mail-Adresse bereits registriert',
    'register.error.weakPassword': 'Passwort zu schwach',
    'register.error.generic': 'Registrierung fehlgeschlagen. Bitte versuchen Sie es erneut.',

    'register.emailSent.title': 'E-Mail gesendet!',
    'register.emailSent.message': 'Bitte überprüfen Sie Ihre E-Mails und bestätigen Sie Ihre Registrierung.',
    'register.emailSent.toLogin': 'Zur Anmeldung',
    'register.emailSent.backToRegister': 'Zurück zur Registrierung',

    'register.success.title': 'Willkommen!',
    'register.success.welcome': 'Erfolgreich registriert,',
    'register.success.toBeta': 'Zur Beta-Anwendung',
    'register.success.logout': 'Abmelden',

    // Footer
    'footer.tagline': 'สร้างด้วยความเคารพต่อศิลปะมวยไทย • Built with respect for the art of Muay Thai',
    'footer.copyright': '© 2025 Muay-Thai.app',
    'footer.copyright.trainiq': '© 2025 TrainIQ',
    'footer.powered': 'powered by TrainIQ',
    'footer.powered.muaythai': 'powered by Muay-Thai.app',
    'footer.version': 'Beta Version',
    'footer.joint': 'Joint Venture zwischen TKBC Asia und CEOPRENEUR Labs',

    // Dashboard Page
    'dashboard.title': 'TrainIQ Dashboard',
    'dashboard.titleShort': 'TrainIQ',
    'dashboard.subtitle': 'Intelligentes Muay Thai Training',

    // Admin Panel
    'admin.title': 'TrainIQ Beta - Moderation',
    'admin.subtitle': 'Anmeldungen warten auf Freigabe',
    'admin.subtitleCleared': 'Keine offenen Freigaben – alles erledigt!',
    'admin.totalUsersLabel': 'Gesamtprofile',
    'admin.loadingPending': 'Lade pendente Anmeldungen...',
    'admin.noRegistrations.title': 'Alle Profile bearbeitet!',
    'admin.noRegistrations.subtitle': 'Keine pendenten Anmeldungen vorhanden.',
    'admin.refresh': 'Aktualisieren',
    'admin.user.age': 'Alter',
    'admin.user.experience': 'Erfahrung',
    'admin.user.goals': 'Trainingsziele',
    'admin.user.registered': 'Angemeldet',
    'admin.user.notes': 'Admin Notizen',
    'admin.user.statusPending': 'Wartet auf Freigabe',
    'admin.user.statusApproved': 'Freigeschaltet',
    'admin.user.statusAuthOnly': 'Registriert',
    'admin.user.statusAwaitingProfile': 'Profil offen',
    'admin.user.missingBetaProfile': 'Beta-Formular noch nicht ausgefüllt. Bitte kontaktiere den User oder fordere das Beta-Profil an, bevor du freischaltest.',
    'admin.user.awaitingProfileCompletion': 'Wartet auf Beta-Setup – keine Moderation möglich.',
    'admin.user.authOnlyRpcMissing': 'Hinweis: Auth-only Benutzer konnten nicht geladen werden. Bitte führe das SQL-Skript supabase-get-auth-only-users.sql im Supabase SQL Editor aus.',
    'admin.user.fallbackName': 'Unbekannt',
    'admin.user.awaitingProfileCompletionPrompt': 'Beta-Zugang freigeschaltet. Der Nutzer muss das Profil noch vervollständigen.',
    'admin.user.alreadyApproved': 'Bereits freigeschaltet',
    'admin.approve': 'Freigeben',
    'admin.reject': 'Ablehnen',
    'admin.rateLimit': 'Zu viele Admin-Aktionen. Bitte warten Sie eine Minute.',
    'admin.invalidUser': 'Ungültige Benutzer-ID',
    'admin.approveError': 'Fehler beim Freigeben',
    'admin.approveSuccess': 'Benutzer erfolgreich freigeschaltet!',
    'admin.rejectError': 'Fehler beim Ablehnen',
    'admin.deletePrompt': 'Benutzer komplett löschen? (Oder nur als abgelehnt markieren)',
    'admin.rejectSuccess': 'Benutzer abgelehnt!',
    'admin.rejectReason': 'Grund für Ablehnung:',
    'admin.accessDenied.title': 'Zugriff verweigert',
    'admin.accessDenied.message': 'Sie sind nicht berechtigt, diese Seite zu besuchen.',

    // Common
    'common.loading': 'Lädt...',
    'common.error': 'Fehler',
    'common.success': 'Erfolgreich',
  },
  en: {
    // Header
    'nav.login': 'Login',
    'nav.beta': 'Join Beta',
    'nav.home': 'Home',

    // Groups (coach view)
    'groups.title': 'Group Training',
    'groups.subtitle': 'Create groups, manage members, and generate optimized group sessions.',
    'groups.create.title': 'New group',
    'groups.create.namePlaceholder': 'Group name',
    'groups.create.submit': 'Create group',
    'groups.empty': 'No groups yet. Create your first group.',
    'groups.members.title': 'Members',
    'groups.members.addPlaceholder': 'Member user ID',
    'groups.members.add': 'Add',
    'groups.members.remove': 'Remove',
    'groups.members.empty': 'No members yet.',
    'groups.generate': 'Generate group session',
    'groups.generating': 'Generating…',
    'groups.session.title': 'Latest generated session',
    'groups.error.generic': 'Something went wrong. Please try again.',
    'groups.error.needTwo': 'A group needs at least two members.',

    // Homepage
    'home.hero.title1': 'The Future of',
    'home.hero.title2': 'Muay Thai Training',
    'home.hero.subtitle': 'Help us develop the first AI for Muay Thai training. Your training data will be used to develop intelligent training plans.',
    'home.hero.cta.beta': 'Join Beta',
    'home.hero.cta.login': 'Login',

    'home.features.title': 'Intelligent Training',
    'home.features.subtitle': 'TrainIQ analyzes your training data and creates personalized plans that adapt to your progress.',

    'home.feature1.title': 'AI-Powered Analysis',
    'home.feature1.desc': 'Our AI analyzes your training habits, progress, and weaknesses to create optimal training plans.',

    'home.feature2.title': 'Personalized Plans',
    'home.feature2.desc': 'Each training plan is individually tailored to your experience level, goals, and available time.',

    'home.feature3.title': 'Adaptive Development',
    'home.feature3.desc': 'The AI continuously learns from your training data and adapts recommendations to your progress.',

    'home.beta.badge': 'Exclusive for selected TKBC members',
    'home.beta.title': 'Be Part of the Revolution',
    'home.beta.subtitle': 'Help us develop the first AI for Muay Thai training. Get exclusive access to new features and shape the future.',
    'home.beta.cta': 'Join Beta Now',

    'home.status.title': 'Current Development Status',
    'home.status.phase': 'Phase 1: Beta Training Tracking & Data Collection',
    'home.status.desc': 'We are currently collecting training data from beta users to train our AI. Personalized training plans and analyses will follow in the next phases.',

    // Beta Page
    'beta.badge': 'Exclusive Beta Program for selected TKBC members',
    'beta.title': 'Muay-Thai.app Training Intelligence',
    'beta.subtitle': 'Help us develop the first AI for Muay Thai training. Your training data helps us create intelligent training plans and progress analyses.',

    'beta.feature1.title': 'Training Tracking',
    'beta.feature1.desc': 'Record your training sessions with duration, content, and notes. Simple and quick input for regular use.',

    'beta.feature2.title': 'AI Development',
    'beta.feature2.desc': 'Your anonymized data flows into the development of personalized training plans and progress analyses.',

    'beta.feature3.title': 'TKBC Community Beta',
    'beta.feature3.desc': 'Exclusive for selected TKBC members: Be part of the first group shaping the future of intelligent Muay Thai training.',

    'beta.privacy.title': 'Moderation & Privacy',
    'beta.privacy.approval': 'Manual Approval: Each registration is manually reviewed and activated. You will receive confirmation once your access is activated.',
    'beta.privacy.data': 'Privacy: All training data is stored encrypted and used exclusively for AI development. No sharing with third parties.',
    'beta.privacy.anon': 'Anonymization: Personal data is separated from training data and processed anonymously for AI analysis.',
    'beta.privacy.delete': 'Deletion: You can request deletion of your data at any time. Contact: beta@muay-thai.app',

    'beta.steps.title': 'How it Works',
    'beta.step1.title': 'Register',
    'beta.step1.desc': 'Create your beta profile with experience level and training goals.',
    'beta.step2.title': 'Activation',
    'beta.step2.desc': 'Wait for manual approval by our team (usually within 24h).',
    'beta.step3.title': 'Track Training',
    'beta.step3.desc': 'Record your training sessions with duration, content, and personal notes.',
    'beta.step4.title': 'Develop AI',
    'beta.step4.desc': 'Your data helps us develop intelligent training plans.',

    'beta.tracker.title': 'Beta Training Tracking',
    'beta.tracker.subtitle': 'Track your Muay Thai training for AI development',

    'beta.info.note': 'Note: This beta version is exclusively for data collection for AI development. Training plans and analyses are not yet available.',

    // Training Tracker
    'training.title': 'Training Tracking',
    'training.hello': 'Hello',
    'training.trackSimple': 'Simply track your training.',
    'training.addTraining': 'Add Training',
    'training.whatTrained': 'What did you train?',
    'training.placeholder': 'e.g. Warm-up 15min, Jab-Cross-Low-Kick 3x3min, Cool-down...',
    'training.duration': 'Duration',
    'training.minutes': 'Minutes',
    'training.date': 'Date',
    'training.today': 'Today',
    'training.notes': 'Notes (optional)',
    'training.notesPlaceholder': 'How did it go? Anything special?',
    'training.rating': 'Rate Training',
    'training.goalHelp': 'Did this training help with your goals?',
    'training.goalHelpful': 'Helpful',
    'training.goalNeutral': 'Neutral',
    'training.goalNotHelpful': 'Not helpful',
    'training.saveTraining': 'Save Training',
    'training.recentTrainings': 'Your Recent Trainings',
    'training.statistics': 'Statistics',
    'training.totalTrainings': 'Total Trainings',
    'training.minutesTrained': 'Minutes Trained',
    'training.templates': 'Use Templates',
    'training.useTemplate': 'Use Template',

    // Trainer Feedback
    'trainer.feedback.title': 'Trainer Feedback',
    'trainer.feedback.submit': 'Submit trainer feedback',
    'trainer.feedback.success': 'Feedback submitted successfully!',
    'trainer.feedback.error': 'Error submitting feedback',
    'trainer.feedback.client': 'Participant',
    'trainer.feedback.session': 'Session',
    'trainer.feedback.validationSelection': 'Please select a session and participant.',
    'trainer.feedback.validationNewSession': 'Please enter a title with at least 3 characters for the new session.',
    'trainer.feedback.validationGuestAlias': 'Please enter at least 2 characters for the guest name.',
    'trainer.feedback.validationScores': 'All score fields must be between 1 and 10.',
    'trainer.feedback.validationWeeks': 'Weeks until milestone must be between 1 and 52.',
    'trainer.feedback.validationActionable': 'Please provide at least one actionable recommendation or observation.',

    'trainer.feedback.clientPerformance': 'Participant performance',
    'trainer.feedback.techniqueQuality': 'Technique quality',
    'trainer.feedback.effortLevel': 'Effort level',
    'trainer.feedback.improvementVisible': 'Improvement visible',
    'trainer.feedback.areasExcelling': 'Areas excelling',
    'trainer.feedback.areasNeedingWork': 'Areas needing work',

    'trainer.feedback.sessionEvaluation': 'Session evaluation',
    'trainer.feedback.difficulty': 'Difficulty',
    'trainer.feedback.tooEasy': 'Too easy',
    'trainer.feedback.optimal': 'Optimal',
    'trainer.feedback.tooHard': 'Too hard',
    'trainer.feedback.duration': 'Duration',
    'trainer.feedback.tooShort': 'Too short',
    'trainer.feedback.tooLong': 'Too long',
    'trainer.feedback.exercisesEffective': 'Exercises were effective',
    'trainer.feedback.progressionSuitable': 'Progression was suitable',
    'trainer.feedback.safetyConcerns': 'Safety concerns',

    'trainer.feedback.progressAssessment': 'Progress assessment',
    'trainer.feedback.skillImprovement': 'Skill improvement',
    'trainer.feedback.fitnessImprovement': 'Fitness improvement',
    'trainer.feedback.motivationLevel': 'Motivation level',
    'trainer.feedback.consistencyRating': 'Consistency rating',
    'trainer.feedback.goalProgress': 'Goal progress',

    'trainer.feedback.recommendations': 'Recommendations',
    'trainer.feedback.nextSessionFocus': 'Next session focus',
    'trainer.feedback.adjustmentsNeeded': 'Adjustments needed',
    'trainer.feedback.equipmentSuggestions': 'Equipment suggestions',
    'trainer.feedback.timelineAdjustments': 'Timeline adjustments',
    'trainer.feedback.accelerate': 'Accelerate',
    'trainer.feedback.maintain': 'Maintain',
    'trainer.feedback.slowDown': 'Slow down',

    'trainer.feedback.coachingNotes': 'Coaching notes',
    'trainer.feedback.positiveObservations': 'Positive observations',
    'trainer.feedback.areasForImprovement': 'Areas for improvement',
    'trainer.feedback.motivationalInsights': 'Motivational insights',
    'trainer.feedback.technicalCorrections': 'Technical corrections',
    'trainer.feedback.overallAssessment': 'Overall assessment',

    'trainer.feedback.futurePlanning': 'Future planning',
    'trainer.feedback.periodizationPhase': 'Periodization phase',
    'trainer.feedback.build': 'Build',
    'trainer.feedback.peak': 'Peak',
    'trainer.feedback.deload': 'Deload',
    'trainer.feedback.weeksUntilMilestone': 'Weeks until next milestone',
    'trainer.feedback.priorityFocus': 'Priority focus',
    'trainer.feedback.priorityFocusPlaceholder': 'e.g. Power development, technique refinement',
    'trainer.feedback.clientModeKnown': 'Existing client',
    'trainer.feedback.clientModeManual': 'Manual',
    'trainer.feedback.clientModeGuest': 'Guest mode',
    'trainer.feedback.sessionModeKnown': 'Existing session',
    'trainer.feedback.sessionModeNew': 'New session',
    'trainer.feedback.newSessionTitle': 'Session title',
    'trainer.feedback.newSessionTitlePlaceholder': 'e.g. Tuesday evening technique block',
    'trainer.feedback.newSessionDate': 'Session date',
    'trainer.feedback.newSessionGeneratedId': 'Generated session ID',
    'trainer.feedback.clientNameOptional': 'Client name (optional)',
    'trainer.feedback.clientNamePlaceholder': 'Client name',
    'trainer.feedback.guestAliasPlaceholder': 'Enter guest name (e.g. Trial Max)',
    'trainer.feedback.guestGeneratedId': 'Generated guest ID',

    // Schedule / Live Session
    'schedule.liveSession': 'Create live session',
    'schedule.scheduleMatch': 'Schedule match',
    'schedule.noSchedule': 'No schedule for this day.',
    'schedule.freeSession': 'Free Session',
    'schedule.startNow': 'Start live session',
    'schedule.createAndFeedback': 'Create session & start feedback',
    'schedule.cancel': 'Cancel',
    'schedule.title': 'Session title',
    'schedule.date': 'Date',
    'schedule.duration': 'Duration (min)',
    'schedule.noMatch': 'No matching schedule entry found. Session will be created as a free session.',
    'schedule.scheduleFor': 'Schedule',

    // Login Page
    'login.title': 'Login',
    'login.subtitle': 'Access to Beta Application',
    'login.email': 'Email Address',
    'login.password': 'Password',
    'login.submit': 'Login',
    'login.loading': 'Logging in...',
    'login.noAccount': 'No access yet?',
    'login.toBeta': 'To Beta Registration',
    'login.error.awaitApproval': 'Your access isn\'t active yet. We manually approve registrations within 24 hours and will email you once it\'s ready.',
    'login.error.emailDisabled': 'Email login is temporarily disabled. Please reach out to beta@trainiq.com for assistance.',
    'login.error.generic': 'Login failed:',

    'login.success.title': 'Logged In!',
    'login.success.welcome': 'Welcome back,',
    'login.success.toBeta': 'To Beta Application',
    'login.success.logout': 'Logout',

    // Register Page
    'register.title': 'Beta Registration',
    'register.subtitle': 'Exclusive access to TrainIQ Beta',
    'register.email': 'Email Address',
    'register.emailPlaceholder': 'your@email.com',
    'register.password': 'Password',
    'register.passwordPlaceholder': 'At least 8 characters',
    'register.confirmPassword': 'Confirm Password',
    'register.confirmPasswordPlaceholder': 'Repeat password',
    'register.submit': 'Register',
    'register.registering': 'Registering...',
    'register.hasAccount': 'Already registered?',
    'register.loginLink': 'Login here',

    'register.error.fillFields': 'Please fill all fields',
    'register.error.passwordMismatch': 'Passwords do not match',
    'register.error.passwordLength': 'Password must be at least 8 characters',
    'register.error.tooManyAttempts': 'Too many attempts. Please wait 15 minutes.',
    'register.error.emailExists': 'Email address already registered',
    'register.error.weakPassword': 'Password too weak',
    'register.error.generic': 'Registration failed. Please try again.',

    'register.emailSent.title': 'Email sent!',
    'register.emailSent.message': 'Please check your email and confirm your registration.',
    'register.emailSent.toLogin': 'To Login',
    'register.emailSent.backToRegister': 'Back to Registration',

    'register.success.title': 'Welcome!',
    'register.success.welcome': 'Successfully registered,',
    'register.success.toBeta': 'To Beta Application',
    'register.success.logout': 'Logout',

    // Footer
    'footer.tagline': 'สร้างด้วยความเคารพต่อศิลปะมวยไทย • Built with respect for the art of Muay Thai',
    'footer.copyright': '© 2025 Muay-Thai.app',
    'footer.copyright.trainiq': '© 2025 TrainIQ',
    'footer.powered': 'powered by TrainIQ',
    'footer.powered.muaythai': 'powered by Muay-Thai.app',
    'footer.version': 'Beta Version',
    'footer.joint': 'Joint Venture between TKBC Asia and CEOPRENEUR Labs',

    // Dashboard Page
    'dashboard.title': 'TrainIQ Dashboard',
    'dashboard.titleShort': 'TrainIQ',
    'dashboard.subtitle': 'Intelligent Muay Thai Training',

    // Admin Panel
    'admin.title': 'TrainIQ Beta - Moderation',
    'admin.subtitle': 'registrations waiting for approval',
    'admin.subtitleCleared': 'No pending approvals – all caught up!',
    'admin.totalUsersLabel': 'Total profiles',
    'admin.loadingPending': 'Loading pending registrations...',
    'admin.noRegistrations.title': 'All profiles processed!',
    'admin.noRegistrations.subtitle': 'No pending registrations available.',
    'admin.refresh': 'Refresh',
    'admin.user.age': 'Age',
    'admin.user.experience': 'Experience',
    'admin.user.goals': 'Training Goals',
    'admin.user.registered': 'Registered',
    'admin.user.notes': 'Admin Notes',
    'admin.user.statusPending': 'Waiting for approval',
    'admin.user.statusApproved': 'Approved',
    'admin.user.statusAuthOnly': 'Registered',
    'admin.user.statusAwaitingProfile': 'Profile incomplete',
    'admin.user.missingBetaProfile': 'Beta form not completed yet. Contact the user or request the beta profile before approval.',
    'admin.user.awaitingProfileCompletion': 'Waiting for beta setup – moderation not available yet.',
    'admin.user.authOnlyRpcMissing': 'Heads-up: Auth-only users could not be loaded. Please run supabase-get-auth-only-users.sql in the Supabase SQL Editor.',
    'admin.user.fallbackName': 'Unknown',
    'admin.user.awaitingProfileCompletionPrompt': 'Beta access granted. The athlete still needs to finish the profile setup.',
    'admin.user.alreadyApproved': 'Already approved',
    'admin.approve': 'Approve',
    'admin.reject': 'Reject',
    'admin.rateLimit': 'Too many admin actions. Please wait one minute.',
    'admin.invalidUser': 'Invalid User ID',
    'admin.approveError': 'Error approving user',
    'admin.approveSuccess': 'User successfully approved!',
    'admin.rejectError': 'Error rejecting user',
    'admin.deletePrompt': 'Delete user completely? (Or just mark as rejected)',
    'admin.rejectSuccess': 'User rejected!',
    'admin.rejectReason': 'Reason for rejection:',
    'admin.accessDenied.title': 'Access Denied',
    'admin.accessDenied.message': 'You are not authorized to visit this page.',

    // Common
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.success': 'Success',
  }
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguage] = useState<Language>('de')

  useEffect(() => {
    const savedLanguage = localStorage.getItem('muay-thai-app-language') as Language
    if (savedLanguage && (savedLanguage === 'de' || savedLanguage === 'en')) {
      setLanguage(savedLanguage)
    }
  }, [])

  const handleSetLanguage = (lang: Language) => {
    setLanguage(lang)
    localStorage.setItem('muay-thai-app-language', lang)
  }

  const t = (key: string): string => {
    const value = (translations[language] as Record<string, string>)[key]
    // Return empty string for unknown keys so component-level fallbacks can render.
    return value ?? ''
  }

  return (
    <LanguageContext.Provider value={{ language, setLanguage: handleSetLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  )
}

export function useLanguage() {
  const context = useContext(LanguageContext)
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
}
